import React, { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { servicesAPI } from '../utils/api';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import AvailabilityModal from '../components/AvailabilityModal';
import { Calendar, Clock, Check, X, ChevronRight, CalendarOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { registerPush } from '../utils/push';
import { firestoreDb } from '../utils/firebase';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';

const formatTime12hr = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hr = hours % 12 || 12;
  return `${hr}:${minutes.toString().padStart(2, '0')} ${ampm}`;
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [myAssignments, setMyAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null); // { id: serviceId, action: 'accept'|'decline' }
  const [showAvailability, setShowAvailability] = useState(false);
  const loadedServiceIdsRef = useRef(new Set());
  // Local date string in YYYY-MM-DD — use local time (not UTC) to avoid timezone boundary bugs
  const _td = new Date();
  const today = `${_td.getFullYear()}-${String(_td.getMonth()+1).padStart(2,'0')}-${String(_td.getDate()).padStart(2,'0')}`;

  const loadData = useCallback(async () => {
    try {
      const _d = new Date();
      const localDate = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;
      const [servicesRes, assignRes] = await Promise.all([
        servicesAPI.getAll({ local_date: localDate }),
        api.get('/services/my-assignments').catch(() => ({ data: [] })),
      ]);
      const sorted = (servicesRes.data.services || []).sort((a, b) => {
        const dateDiff = a.date.localeCompare(b.date);
        if (dateDiff !== 0) return dateDiff;
        return (a.time || '00:00').localeCompare(b.time || '00:00');
      });
      setServices(sorted);
      loadedServiceIdsRef.current = new Set(sorted.map(s => s.service_id));
      const map = {};
      (assignRes.data || []).forEach(a => { map[a.service_id] = a; });
      setMyAssignments(map);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    if (user?.account_status === 'active') registerPush();
  }, [loadData, user]);

  // Real-time: reflect service additions, updates, and deletions without a page refresh.
  useEffect(() => {
    if (!firestoreDb) return;
    let unsubscribe;
    try {
      // Cap at 90 docs — prevents reading unlimited future services on initial snapshot.
      const q = query(
        collection(firestoreDb, 'services'),
        where('date', '>=', today),
        orderBy('date', 'asc'),
        limit(90)
      );
      unsubscribe = onSnapshot(q, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const doc = change.doc.data();
          if (!doc.service_id || !doc.date) return;

          if (change.type === 'added' || change.type === 'modified') {
            setServices(prev => {
              const exists = prev.some(s => s.service_id === doc.service_id);
              if (exists) {
                // Update existing — preserve server-computed can_edit
                return prev.map(s =>
                  s.service_id === doc.service_id ? { ...s, ...doc, can_edit: s.can_edit } : s
                );
              }
              // Truly new service added by another user
              if (!loadedServiceIdsRef.current.has(doc.service_id)) {
                loadedServiceIdsRef.current.add(doc.service_id);
                const updated = [...prev, { ...doc, can_edit: false }];
                return updated.sort((a, b) =>
                  a.date.localeCompare(b.date) || (a.time || '').localeCompare(b.time || '')
                );
              }
              return prev;
            });
          } else if (change.type === 'removed') {
            loadedServiceIdsRef.current.delete(doc.service_id);
            setServices(prev => prev.filter(s => s.service_id !== doc.service_id));
          }
        });
      }, () => { /* Firestore unavailable — silent fail */ });
    } catch { /* Firestore not configured */ }
    return () => { if (unsubscribe) unsubscribe(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRespond = async (serviceId, status, action) => {
    flushSync(() => setResponding({ id: serviceId, action }));
    try {
      await api.post('/services/respond', { service_id: serviceId, status });
      if (status !== 'pending') {
        toast.success(status === 'accepted' ? 'Accepted' : 'Declined');
      }
      setMyAssignments(prev => ({
        ...prev,
        [serviceId]: { ...prev[serviceId], status }
      }));
    } catch {
      toast.error('Failed to respond');
    } finally {
      setResponding(null);
    }
  };

  const allUpcoming = services.filter((s) => s.date >= today);
  const upcomingServices = allUpcoming.slice(0, 1);
  const remaining = allUpcoming.length - 1;

  return (
    <>
    <div className="bg-slate-50 pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
        <div className="mb-6 sm:mb-8 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-green-700 mb-1">Dashboard</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="mt-1 text-sm text-slate-500">Here's what's coming up for your services</p>
          </div>
          <button
            onClick={() => setShowAvailability(true)}
            data-testid="my-availability-btn-dashboard"
            className="flex-shrink-0 h-9 px-3 rounded-full border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all text-sm font-medium flex items-center gap-1.5 shadow-sm mt-1"
          >
            <CalendarOff className="w-4 h-4" />
            <span className="hidden sm:inline">My Availability</span>
          </button>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4" data-testid="upcoming-heading">Upcoming Services</h2>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700 mx-auto"></div>
            </div>
          ) : upcomingServices.length === 0 ? (
            <div className="text-center py-12 bg-white border border-slate-200 rounded-xl">
              <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No upcoming services scheduled</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="upcoming-services-list">
              {upcomingServices.map((service) => {
                const assignment = myAssignments[service.service_id];
                const isAssigned = !!assignment;
                const status = assignment?.status;
                const roles = assignment?.roles || [];

                return (
                  <div key={service.service_id} data-testid={`dashboard-service-${service.service_id}`}
                    className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:border-green-200 transition-all duration-200">
                    {/* Clickable service info */}
                    <div onClick={() => navigate(`/services/${service.service_id}`)}
                      className="flex items-center gap-4 px-5 py-4 cursor-pointer group">
                      {/* Date block */}
                      <div className="shrink-0 w-14 h-14 rounded-lg bg-green-50 flex flex-col items-center justify-center" data-testid={`service-date-${service.service_id}`}>
                        <span className="text-xs font-semibold text-green-600 uppercase leading-none">
                          {format(new Date(service.date), 'MMM')}
                        </span>
                        <span className="text-xl font-bold text-green-700 leading-tight">
                          {format(new Date(service.date), 'dd')}
                        </span>
                      </div>
                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 group-hover:text-green-700 transition-colors truncate">
                          {service.name || service.service_type}
                        </p>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
                          <span className="font-medium text-slate-600 truncate">{service.service_type}</span>
                          <span className="flex items-center gap-1 shrink-0">
                            <Clock className="w-3 h-3" />{formatTime12hr(service.time)}
                          </span>
                          <span className="shrink-0">{format(new Date(service.date), 'EEEE')}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-green-400 transition-colors shrink-0" />
                    </div>

                    {/* Assignment bar */}
                    {isAssigned && (
                      <div className="px-5 py-3 bg-slate-50 border-t border-slate-100" data-testid={`assignment-bar-${service.service_id}`}>
                          <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-600">
                              You're assigned: <span className="text-slate-800">{roles.join(', ')}</span>
                            </p>
                          </div>
                          {service.date < today ? (
                            <p className="text-xs text-slate-400 italic shrink-0">Service passed</p>
                          ) : (
                          <div className="inline-flex rounded-md overflow-hidden border border-slate-200 shrink-0" data-testid={`response-toggle-${service.service_id}`}>
                              <button data-testid={`accept-btn-${service.service_id}`}
                                onClick={(e) => { e.stopPropagation(); handleRespond(service.service_id, status === 'accepted' ? 'pending' : 'accepted', 'accept'); }}
                                disabled={responding?.id === service.service_id}
                                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                                  status === 'accepted' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-400 hover:bg-emerald-50 hover:text-emerald-600'
                                }`}>
                                {responding?.id === service.service_id && responding?.action === 'accept'
                                  ? <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                  : <Check className="w-3 h-3" />
                                }
                                {status === 'accepted' ? 'Accepted' : 'Accept'}
                              </button>
                              <button data-testid={`decline-btn-${service.service_id}`}
                                onClick={(e) => { e.stopPropagation(); handleRespond(service.service_id, status === 'declined' ? 'pending' : 'declined', 'decline'); }}
                                disabled={responding?.id === service.service_id}
                                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors border-l border-slate-200 disabled:opacity-60 disabled:cursor-not-allowed ${
                                  status === 'declined' ? 'bg-rose-600 text-white' : 'bg-white text-slate-400 hover:bg-rose-50 hover:text-rose-600'
                                }`}>
                                {responding?.id === service.service_id && responding?.action === 'decline'
                                  ? <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                  : <X className="w-3 h-3" />
                                }
                                {status === 'declined' ? 'Declined' : 'Decline'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {remaining > 0 && (
                <button
                  onClick={() => navigate('/services')}
                  data-testid="view-remaining-services-btn"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:border-green-300 hover:text-green-700 hover:bg-green-50 transition-all duration-200"
                >
                  See {remaining} more service{remaining !== 1 ? 's' : ''} for you
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    {showAvailability && <AvailabilityModal onClose={() => setShowAvailability(false)} />}
    </>
  );
};

export default Dashboard;
